// types/prescription.ts - Clean and consistent type definitions

import { format } from "date-fns";
import { DateUtil } from "@/utils/dateUtil.ts";

// ─── Core interfaces ──────────────────────────────────────────────────────────

export interface Prescription {
  id: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  date: string;
  diagnosis?: string;
  examination?: string;
  quick_notes?: string;
  advice?: string;
  created_at?: string;
  updated_at?: string;
  last_modified?: string;
  synced_at?: string | null;
  patient_name?: string;
  // Joined by the list endpoints (searchPrescriptions) so rows can show patient basics
  patient_age?: number | null;
  patient_date_of_birth?: string | null;
  patient_gender?: string | null;
  patient_phone?: string | null;
  doctor_name?: string;
  clinic_name?: string;
  /** Sent with a single prescription: whether the signed-in user may change it, and why not */
  edit_policy?: { allowed: boolean; message: string | null };
  vitals?: PrescriptionVitals;
  complaints?: PrescriptionComplaint[];
  medicines?: PrescriptionMedicine[];
  investigations?: string[];
  next_visit?: PrescriptionNextVisit;
  custom_fields?: PrescriptionCustomFields;
}

export interface PrescriptionCustomFields {
  custom_1?: string;
  custom_2?: string;
  custom_3?: string;
}

export interface CustomFieldConfig {
  key: "custom_1" | "custom_2" | "custom_3";
  label: string;
  enabled: boolean;
  /** Section the field follows on the Rx pad and the printout; absent = at the end */
  after?: string;
}

export interface PrescriptionLayoutConfig {
  hiddenSections: string[];
  customFields: CustomFieldConfig[];
}

export interface PrescriptionVitals {
  bp?: string;
  pulse?: string | number;   // stored as SMALLINT in DB; always convert to string before form use
  spo2?: string | number;    // stored as TINYINT in DB
  weight?: string | number;  // stored as DECIMAL(5,2) in DB
  temp?: string | number;    // stored as DECIMAL(4,1) in DB
  edema?: string;
  pallor?: string;
  icterus?: string;
}

export interface PrescriptionComplaint {
  id?: string;
  complaint: string;
  frequency?: string;
  severity?: string;
  duration?: string;
  date?: string;
}

export interface PrescriptionMedicine {
  id?: string;
  drug_id?: number | null;
  brand_name?: string;
  generic_name?: string;
  product?: string;
  dosage?: string;
  taken_when?: string;
  route?: string;
  duration?: string;
  quantity?: string | number;
  notes?: string;
}

/**
 * Form-only medicine row — extends PrescriptionMedicine with UI-only drug
 * meta fields used for dosage option resolution. These are never submitted
 * to the backend (stripped in toApiPayload).
 */
export interface PrescriptionMedicineFormRow extends PrescriptionMedicine {
  _drug_form?: string;
  _drug_brand?: string;
}

export interface PrescriptionNextVisit {
  date?: string;
  time?: string;
  notes?: string;
}

// ─── Form data ────────────────────────────────────────────────────────────────

export interface PrescriptionFormData {
  id?: string;
  patient_id: string;
  doctor_id: string;
  clinic_id: string;
  date: string;
  diagnosis: string;
  examination: string;
  quick_notes: string;
  advice: string;
  vitals: PrescriptionVitals;
  complaints: PrescriptionComplaint[];
  /** Uses PrescriptionMedicineFormRow in the form — _drug_form/_drug_brand stripped on submit */
  medicines: PrescriptionMedicineFormRow[];
  investigations: string[];
  next_visit: PrescriptionNextVisit;
  custom_fields: PrescriptionCustomFields;
}

export interface Patient {
  id: string;
  name: string;
  age?: number;
  gender?: string;
  phone?: string;
  created_at?: string;
  updated_at?: string;
  last_modified?: string;
}

export interface PrescriptionFormProps {
  mode: "create" | "edit";
  patient_id: string;
  prescription?: Prescription;
  onSaved?: () => void;
  onCancel?: () => void;
}

export interface UserSettings {
  profile: {
    name: string;
    email: string;
    phone: string;
    specialty: string;
    license: string;
    signature: string;
  };
  clinic: {
    name: string;
    address: string;
    phone: string;
    email: string;
  };
  preferences: {
    theme: "light" | "dark" | "system";
    language: string;
    notifications: boolean;
    autoSave: boolean;
    printFormat: "a4" | "letter" | "compact";
  };
  prescriptionLayout: PrescriptionLayoutConfig;
}

// ─── Data transformer ─────────────────────────────────────────────────────────

export class PrescriptionDataTransformer {

  static toFormData(prescription: Prescription, patient_id: string): PrescriptionFormData {
    return {
      id:          prescription.id,
      patient_id,
      doctor_id:   prescription.doctor_id  || "",
      clinic_id:   prescription.clinic_id  || "",
      date:        prescription.date        || format(new Date(), "yyyy-MM-dd"),
      diagnosis:   prescription.diagnosis   || "",
      examination: prescription.examination || "",
      quick_notes: prescription.quick_notes || "",
      advice:      prescription.advice      || "",

      vitals: prescription.vitals
          ? {
            bp:      prescription.vitals.bp      ?? "",
            pulse:   prescription.vitals.pulse   != null ? String(prescription.vitals.pulse)  : "",
            spo2:    prescription.vitals.spo2    != null ? String(prescription.vitals.spo2)   : "",
            weight:  prescription.vitals.weight  != null ? String(prescription.vitals.weight) : "",
            temp:    prescription.vitals.temp    != null ? String(prescription.vitals.temp)   : "",
            edema:   prescription.vitals.edema   ?? "",
            pallor:  prescription.vitals.pallor  ?? "",
            icterus: prescription.vitals.icterus ?? "",
          }
          : { bp: "", pulse: "", spo2: "", weight: "", temp: "", edema: "", pallor: "", icterus: "" },

      complaints: prescription.complaints?.length
          ? prescription.complaints.map((c) => ({
            ...c,
            complaint: c.complaint ?? "",
            frequency: c.frequency ?? "",
            severity:  c.severity  ?? "",
            duration:  c.duration  ?? "",
            date:      DateUtil.getDateOnlyFormat(c.date) ?? "",
          }))
          : [{ complaint: "", frequency: "", severity: "", duration: "", date: "" }],

      medicines: prescription.medicines?.length
          ? prescription.medicines.map((m) => ({
            product:      m.product      || "",
            brand_name:   m.brand_name   || m.product || "",
            generic_name: m.generic_name ?? "",
            drug_id:      m.drug_id      ?? null,
            notes:        m.notes        ?? "",
            dosage:       m.dosage       ?? "",
            taken_when:   m.taken_when   ?? "",
            route:        m.route        ?? "",
            duration:     m.duration     ?? "",
            quantity:     m.quantity     ?? "",
            // Drug meta not present in saved prescriptions — leave empty;
            // MedicineListForm will populate on fresh drug selection
            _drug_form:  "",
            _drug_brand: "",
          }))
          : [{ drug_id: null, product: "", brand_name: "", generic_name: "", dosage: "", taken_when: "", route: "", duration: "", quantity: "", notes: "", _drug_form: "", _drug_brand: "" }],

      investigations: prescription.investigations || [],

      next_visit: prescription.next_visit
          ? {
            date:  prescription.next_visit.date  ?? "",
            time:  prescription.next_visit.time  ?? "",
            notes: prescription.next_visit.notes ?? "",
          }
          : { date: "", time: "", notes: "" },

      custom_fields: {
        custom_1: prescription.custom_fields?.custom_1 ?? "",
        custom_2: prescription.custom_fields?.custom_2 ?? "",
        custom_3: prescription.custom_fields?.custom_3 ?? "",
      },
    };
  }

  /**
   * Build form data for a copied prescription.
   * Copies: diagnosis, medicines, investigations, advice.
   * Resets: id, vitals, complaints, examination, quick_notes, next_visit.
   */
  static toCopyData(
    prescription: Prescription,
    patient_id: string,
    doctor_id: string,
    clinic_id: string,
  ): PrescriptionFormData {
    const EMPTY_MEDICINE = {
      drug_id: null, product: "", brand_name: "", generic_name: "",
      dosage: "", taken_when: "", route: "", duration: "", quantity: "", notes: "",
      _drug_form: "", _drug_brand: "",
    };
    const EMPTY_VITALS = {
      bp: "", pulse: "", spo2: "", weight: "", temp: "",
      edema: "", pallor: "", icterus: "",
    };

    return {
      // New identity
      id:         undefined as any,
      patient_id,
      doctor_id,
      clinic_id,
      date:       format(new Date(), "yyyy-MM-dd"),

      // Copied clinical content
      diagnosis:      prescription.diagnosis  || "",
      advice:         prescription.advice     || "",
      investigations: prescription.investigations || [],

      medicines: prescription.medicines?.length
        ? prescription.medicines
            .filter((m) => m?.product?.trim())
            .map((m) => ({
              product:      m.product      || "",
              brand_name:   m.brand_name   || m.product || "",
              generic_name: m.generic_name || "",
              drug_id:      m.drug_id      ?? null,
              dosage:       m.dosage       || "",
              taken_when:   m.taken_when   || "",
              route:        m.route        || "",
              duration:     m.duration     || "",
              quantity:     m.quantity     || "",
              notes:        m.notes        || "",
              _drug_form:   "",
              _drug_brand:  "",
            }))
        : [EMPTY_MEDICINE],

      // Always reset for new visit
      vitals:      EMPTY_VITALS,
      complaints:  [{ complaint: "", frequency: "", severity: "", duration: "", date: "" }],
      examination: "",
      quick_notes: "",
      next_visit:  { date: "", time: "", notes: "" },
      custom_fields: { custom_1: "", custom_2: "", custom_3: "" },
    };
  }

  /**
   * Convert form data to API payload.
   * Explicitly strips UI-only fields (_drug_form, _drug_brand) so the
   * backend schema (additionalProperties: false) never rejects the request.
   */
  static toApiPayload(formData: PrescriptionFormData, isUpdate = false): Prescription {
    const formatDate = (dateStr: string): string => {
      if (!dateStr) return "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      if (dateStr.includes("T")) return dateStr.split("T")[0];
      if (dateStr.includes(" ")) return dateStr.split(" ")[0];
      return dateStr;
    };

    const payload: any = {
      patient_id:  formData.patient_id,
      doctor_id:   formData.doctor_id,
      clinic_id:   formData.clinic_id,
      date:        formatDate(formData.date),
      diagnosis:   formData.diagnosis?.trim()   || "",
      examination: formData.examination?.trim() || "",
      quick_notes: formData.quick_notes?.trim() || "",
      advice:      formData.advice?.trim()      || "",
      vitals:      formData.vitals,
      complaints:  formData.complaints.filter((c) => c.complaint?.trim()),
      // Explicitly destructure to strip _drug_form and _drug_brand
      medicines: formData.medicines
          .filter((m) => (m.brand_name || m.product)?.trim())
          .map(({ _drug_form, _drug_brand, ...apiFields }) => apiFields),
      investigations: formData.investigations.filter((i) => i?.trim()),
      next_visit:     formData.next_visit,
      custom_fields: {
        custom_1: formData.custom_fields?.custom_1?.trim() || "",
        custom_2: formData.custom_fields?.custom_2?.trim() || "",
        custom_3: formData.custom_fields?.custom_3?.trim() || "",
      },
    };

    if (formData.id) payload.id = formData.id;

    return payload;
  }

  static createEmpty(patient_id: string, doctor_id: string, clinic_id: string): PrescriptionFormData {
    return {
      patient_id,
      doctor_id:   doctor_id  || "",
      clinic_id:   clinic_id  || "",
      date:        format(new Date(), "yyyy-MM-dd"),
      diagnosis:   "",
      examination: "",
      quick_notes: "",
      advice:      "",
      vitals: { bp: "", pulse: "", spo2: "", weight: "", temp: "", edema: "", pallor: "", icterus: "" },
      complaints:  [{ complaint: "", frequency: "", severity: "", duration: "", date: "" }],
      medicines:   [{ drug_id: null, product: "", brand_name: "", generic_name: "", dosage: "", taken_when: "", route: "", duration: "", quantity: "", notes: "", _drug_form: "", _drug_brand: "" }],
      investigations: [],
      next_visit:  { date: "", time: "", notes: "" },
      custom_fields: { custom_1: "", custom_2: "", custom_3: "" },
    };
  }
}